/* eslint-disable max-statements */

import { Button, Group, LoadingOverlay, Stack, Text } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { useMemo, useState } from 'react';
import { z } from 'zod';

import { TemplateType, TransitGroup } from '@e-origin/shared';

import { Select, TextInput } from '@components';
import { useCustomersNamesAndIds } from '@hooks/customer';
import { useCreateTransit } from '@hooks/transit';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useTemplates } from '@hooks/useTemplates';

interface NewTransitProps {
  isUpdate?: boolean;
  onHide(): void;
  afterCreate(): void;
  transitView: TransitGroup;
}

const transitSchema = z
  .object({
    name: z
      .string()
      .min(1, { message: 'Required' })
      .regex(/^[a-zA-Z0-9-]*$/g, { message: 'Name is invalid' }),
    customer: z.string().min(1, { message: 'Required' }),
    template: z.string().min(1, { message: 'Required' }),
    MRN: z.string().optional(),
    group: z.nativeEnum(TransitGroup),
  })
  .superRefine((data, ctx) => {
    if (data.group === TransitGroup.ARRIVAL && data.MRN?.length < 1) {
      ctx.addIssue({
        path: ['MRN'],
        message: 'MRN is required',
        code: 'custom',
      });
    }
  });

type TransitFormValues = z.infer<typeof transitSchema>;

export const NewTransit: React.FC<NewTransitProps> = ({ isUpdate, onHide, afterCreate, transitView }) => {
  const [submitting, setSubmitting] = useState(false);
  const customersNamesAndIds = useCustomersNamesAndIds();
  const createTransitMutation = useCreateTransit();
  const templateType = transitView === TransitGroup.ARRIVAL ? TemplateType.ARRIVAL_TRANSIT : TemplateType.DEPARTURE_TRANSIT;
  const { data: templates, isLoading } = useTemplates({ filters: { type: templateType, deleted: false } });

  const templatesList = useMemo(
    () =>
      templates.map((template) => ({
        label: template.name,
        value: template._id,
      })),
    [templates],
  );

  const form = useFormWithNameInputProp<TransitFormValues>({
    initialValues: {
      name: '',
      customer: '',
      template: '',
      MRN: '',
      group: transitView,
    },
    validate: zodResolver(transitSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = async (values: TransitFormValues) => {
    try {
      setSubmitting(true);
      await createTransitMutation.mutateAsync({ transit: values });
      afterCreate();
      onHide();
    } catch (err) {
      // silent fail - toast handled
    } finally {
      setSubmitting(false);
    }
  };

  const isCustomersNamesAndIdsEmpty = customersNamesAndIds.length === 0;

  if (!templatesList) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm" mb="xl">
        <TextInput {...form.getInputProps('name')} label="Transit name" disabled={isUpdate} size="lg" data-pw="transit-name-input" />

        {!isCustomersNamesAndIdsEmpty && (
          <Select
            {...form.getInputProps('customer')}
            label="Select customer"
            data={customersNamesAndIds}
            disabled={isUpdate}
            searchable
            size="lg"
            maxDropdownHeight={200}
            data-pw="select-customer-dropdown"
          />
        )}

        <Select
          {...form.getInputProps('template')}
          label="Select template"
          data={templatesList}
          disabled={isUpdate}
          searchable
          size="lg"
          maxDropdownHeight={200}
          data-pw="select-template-dropdown"
        />

        {templateType === TemplateType.ARRIVAL_TRANSIT && (
          <TextInput
            {...form.getInputProps('MRN')}
            value={form.values.MRN}
            onChange={(event) => {
              form.setFieldValue('MRN', (event.target.value || '').trim());
            }}
            label="Transit MRN"
            disabled={isUpdate}
            size="lg"
            data-pw="transit-mrn-input"
          />
        )}

        {isCustomersNamesAndIdsEmpty && <Text c="#FF0000">You don't have any customers. Please, create a customer.</Text>}
      </Stack>

      <Group justify="center">
        <Button type="button" onClick={onHide} disabled={submitting} size="lg" variant="transparent">
          Cancel
        </Button>

        <Button type="submit" loading={submitting} disabled={!form.isValid() || isCustomersNamesAndIdsEmpty} variant="filled" size="lg" data-pw="create-btn">
          Create
        </Button>
      </Group>
    </form>
  );
};
